// Constants for the Gallery Platform

// =============================================================================
// API Configuration
// =============================================================================

export const API_VERSION = 'v1';

// =============================================================================
// Media Lease Configuration
// =============================================================================

export const MEDIA_LEASE_TTL_SECONDS = 5 * 60; // 5 minutes
export const LEASE_TTL_MS = MEDIA_LEASE_TTL_SECONDS * 1000;
export const LEASE_RENEW_THRESHOLD_MS = 60 * 1000; // Renew when 1 minute left

// =============================================================================
// Rate Limiting
// =============================================================================

export const RATE_LIMITS = {
  LEASE_PER_IP_PER_MINUTE: 60,
  LEASE_PER_USER_PER_MINUTE: 120,
  AUTH_ATTEMPTS_PER_IP_PER_MINUTE: 10,
  UPLOAD_REQUESTS_PER_USER_PER_HOUR: 100,
} as const;

// =============================================================================
// Session Configuration
// =============================================================================

export const SESSION_TTL_SECONDS = 7 * 24 * 60 * 60; // 7 days
export const SESSION_COOKIE_NAME = 'gallery_session';

// =============================================================================
// Room/Pairing Configuration
// =============================================================================

export const ROOM_CODE_LENGTH = 6;
export const ROOM_DEFAULT_EXPIRY_HOURS = 24;
export const DRIFT_BROADCAST_INTERVAL_MS = 5000; // 5 seconds
export const DRIFT_MAX_ACCEPTABLE_MS = 500; // Force seek if drift > 500ms

// =============================================================================
// Playback Configuration
// =============================================================================

export const DEFAULT_IMAGE_DURATION_MS = 10000; // 10 seconds
export const DEFAULT_TRANSITION_DURATION_MS = 1000; // 1 second fade
export const PRELOAD_AHEAD_COUNT = 2; // Preload next 2 items

// =============================================================================
// Media Configuration
// =============================================================================

export const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'] as const;
export const ALLOWED_VIDEO_TYPES = ['video/mp4', 'video/quicktime', 'video/webm'] as const;
export const MAX_IMAGE_SIZE_MB = 50;
export const MAX_VIDEO_SIZE_MB = 500;
export const MAX_VIDEO_DURATION_MINUTES = 30;

// =============================================================================
// Thumbnail Sizes
// =============================================================================

export const THUMBNAIL_SIZES = {
  small: { width: 200, height: 200 },
  medium: { width: 400, height: 400 },
  large: { width: 800, height: 800 },
  hero: { width: 1200, height: 800 },
} as const;

// =============================================================================
// Event Package Configuration
// =============================================================================

export const EVENT_PACKAGES = {
  basic: {
    name: 'Basic',
    maxScreens: 1,
    maxDurationHours: 4,
    features: ['Single screen playback', 'Curated playlist', 'Phone remote control'],
  },
  pro: {
    name: 'Pro',
    maxScreens: 4,
    maxDurationHours: 8,
    features: [
      'Multi-screen zones',
      'Synced playback',
      'Custom transitions',
      'Priority support',
    ],
  },
  immersive: {
    name: 'Immersive',
    maxScreens: 10,
    maxDurationHours: 24,
    features: [
      'All Pro features',
      'Live generative layer',
      'Venue integration',
      'Dedicated technician',
    ],
  },
} as const;

// =============================================================================
// Stripe Configuration
// =============================================================================

export const STRIPE_PLATFORM_FEE_PERCENT = 10; // 10% platform fee
export const STRIPE_MINIMUM_CHARGE_CENTS = 100; // $1.00 minimum

// =============================================================================
// NFT Configuration
// =============================================================================

export const NFT_METADATA_STANDARD = 'metaplex';
export const NFT_ROYALTY_BASIS_POINTS = 500; // 5% royalty

// =============================================================================
// WebSocket Message Types
// =============================================================================

export const WS_MESSAGE_TYPES = {
  // Client -> Server
  HELLO_SCREEN: 'HELLO_SCREEN',
  HELLO_CONTROLLER: 'HELLO_CONTROLLER',
  PAIR_CLAIM: 'PAIR_CLAIM',
  CMD: 'CMD',
  HEARTBEAT: 'HEARTBEAT',

  // Server -> Client
  PAIR_STATUS: 'PAIR_STATUS',
  STATE: 'STATE',
  CMD_ACK: 'CMD_ACK',
  DRIFT: 'DRIFT',
  ERROR: 'ERROR',
} as const;

export const COMMAND_TYPES = {
  START: 'START',
  PAUSE: 'PAUSE',
  NEXT: 'NEXT',
  PREV: 'PREV',
  SEEK: 'SEEK',
  SET_PLAYLIST: 'SET_PLAYLIST',
  SET_ZONE_LAYOUT: 'SET_ZONE_LAYOUT',
} as const;

// =============================================================================
// Error Codes
// =============================================================================

export const ERROR_CODES = {
  // Auth
  INVALID_CREDENTIALS: 'INVALID_CREDENTIALS',
  SESSION_EXPIRED: 'SESSION_EXPIRED',
  UNAUTHORIZED: 'UNAUTHORIZED',
  FORBIDDEN: 'FORBIDDEN',
  INVALID_TOKEN: 'INVALID_TOKEN',

  // Validation
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  INVALID_INPUT: 'INVALID_INPUT',

  // Resources
  NOT_FOUND: 'NOT_FOUND',
  ALREADY_EXISTS: 'ALREADY_EXISTS',
  CONFLICT: 'CONFLICT',

  // Entitlements
  NO_ENTITLEMENT: 'NO_ENTITLEMENT',
  ENTITLEMENT_EXPIRED: 'ENTITLEMENT_EXPIRED',

  // Rate Limiting
  RATE_LIMITED: 'RATE_LIMITED',
  TOO_MANY_REQUESTS: 'TOO_MANY_REQUESTS',

  // Security
  CAPTCHA_FAILED: 'CAPTCHA_FAILED',
  CAPTCHA_REQUIRED: 'CAPTCHA_REQUIRED',

  // Media
  LEASE_EXPIRED: 'LEASE_EXPIRED',
  MEDIA_NOT_FOUND: 'MEDIA_NOT_FOUND',
  UPLOAD_FAILED: 'UPLOAD_FAILED',

  // Room/Pairing
  ROOM_NOT_FOUND: 'ROOM_NOT_FOUND',
  ROOM_EXPIRED: 'ROOM_EXPIRED',
  CONTROLLER_CLAIMED: 'CONTROLLER_CLAIMED',

  // Payment
  PAYMENT_FAILED: 'PAYMENT_FAILED',
  REFUND_FAILED: 'REFUND_FAILED',

  // Server
  INTERNAL_ERROR: 'INTERNAL_ERROR',
  SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',
} as const;
